// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from "@tanstack/react-query";
import { useHttpClient } from "@toggl/sync/http";
import {
  CandidateauthAuthRequest,
  CandidateauthMagicLinkRequest,
  CandidateauthTestLoginRequest,
  CandidateUpdateCandidateParams,
} from "../types";
import { MutationOptions } from "../utils/mutations";
import {
  patchCandidateMeMutationKey,
  postCandidateLogoutMutationKey,
  postMagicLinkMutationKey,
  postPortalLoginMutationKey,
  postProviderRedirectMutationKey,
  postXLoginMutationKey,
} from "./keys";
import {
  patchCandidateMe,
  postCandidateLogout,
  postMagicLink,
  postPortalLogin,
  postProviderRedirect,
  postXLogin,
} from "./requests";

export type PostMagicLinkMutationVariables = {
  data: CandidateauthMagicLinkRequest;
};

export type PostMagicLinkMutationOptions<TContext = unknown> = MutationOptions<
  typeof postMagicLink,
  PostMagicLinkMutationVariables,
  TContext
>;

/**
 * This method processes a request to generate and send a magic link token.
 */
export function usePostMagicLinkMutation<TContext = unknown>(
  options: PostMagicLinkMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ data }) => postMagicLink(httpClient, data),
    mutationKey: postMagicLinkMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postMagicLink mutation. */
export function usePostMagicLinkMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postMagicLinkMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PostMagicLinkMutationVariables,
  });
}

export type PostPortalLoginMutationVariables = {
  data: CandidateauthAuthRequest;
};

export type PostPortalLoginMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postPortalLogin,
    PostPortalLoginMutationVariables,
    TContext
  >;

/**
 * Authenticates candidate portal candidates, setting a candidate authenticated cookie with the candidate user info.
 */
export function usePostPortalLoginMutation<TContext = unknown>(
  options: PostPortalLoginMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ data }) => postPortalLogin(httpClient, data),
    mutationKey: postPortalLoginMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postPortalLogin mutation. */
export function usePostPortalLoginMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postPortalLoginMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PostPortalLoginMutationVariables,
  });
}

export type PostXLoginMutationVariables = {
  data: CandidateauthTestLoginRequest;
};

export type PostXLoginMutationOptions<TContext = unknown> = MutationOptions<
  typeof postXLogin,
  PostXLoginMutationVariables,
  TContext
>;

/**
 * Test candidate authentication, sets a candidate authenticated cookie with the provided payload data, should not be used in production.
 */
export function usePostXLoginMutation<TContext = unknown>(
  options: PostXLoginMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ data }) => postXLogin(httpClient, data),
    mutationKey: postXLoginMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postXLogin mutation. */
export function usePostXLoginMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postXLoginMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PostXLoginMutationVariables,
  });
}

export type PostProviderRedirectMutationVariables = {
  provider: "github" | "linkedin";
  data: CandidateauthAuthRequest;
  error?: string;
};

export type PostProviderRedirectMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postProviderRedirect,
    PostProviderRedirectMutationVariables,
    TContext
  >;

/**
 * Handles OAuth authentication requests, ensuring the provider is valid, and initiating an authentication session.
 */
export function usePostProviderRedirectMutation<TContext = unknown>(
  options: PostProviderRedirectMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ provider, data, error }) =>
      postProviderRedirect(httpClient, provider, data, error),
    mutationKey: postProviderRedirectMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postProviderRedirect mutation. */
export function usePostProviderRedirectMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: postProviderRedirectMutationKey,
      status: "pending",
    },
    select: (mutation) =>
      mutation.state.variables as PostProviderRedirectMutationVariables,
  });
}

export type PostCandidateLogoutMutationVariables = { job_opening_slug: string };

export type PostCandidateLogoutMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postCandidateLogout,
    PostCandidateLogoutMutationVariables,
    TContext
  >;

/**
 * Logs out a candidate by setting the cookie expiration date as the current time
 */
export function usePostCandidateLogoutMutation<TContext = unknown>(
  options: PostCandidateLogoutMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug }) =>
      postCandidateLogout(httpClient, job_opening_slug),
    mutationKey: postCandidateLogoutMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postCandidateLogout mutation. */
export function usePostCandidateLogoutMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postCandidateLogoutMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PostCandidateLogoutMutationVariables,
  });
}

export type PatchCandidateMeMutationVariables = {
  job_opening_slug: string;
  data: Partial<CandidateUpdateCandidateParams>;
};

export type PatchCandidateMeMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof patchCandidateMe,
    PatchCandidateMeMutationVariables,
    TContext
  >;

/**
 * Updates contact information for the current authenticated candidate.
 */
export function usePatchCandidateMeMutation<TContext = unknown>(
  options: PatchCandidateMeMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, data }) =>
      patchCandidateMe(httpClient, job_opening_slug, data),
    mutationKey: patchCandidateMeMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending patchCandidateMe mutation. */
export function usePatchCandidateMeMutationVariables() {
  return useMutationState({
    filters: { mutationKey: patchCandidateMeMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PatchCandidateMeMutationVariables,
  });
}
